import { DeviceType } from "common/types";
import { EventType } from "safetySensors/deviceAlerts";
import { IMeasuringDeviceAlert, INodeSensor } from "safetySensors/types";
import {
  installationsOnNodeUrl,
  measuringDeviceAlertUrl,
  meterEventsSubscriptionsUrl,
  meterEventsUrl,
} from "../common/infrastructure/endpoints";
import { get, put } from "../common/infrastructure/requests";
import { IApplicationState } from "../redux/root-reducer";
import { actionCreator } from "./meter.reducer";
import {
  IMeterEventsObject,
  IMeterEventSubscriptionObject,
  IMeterObject,
} from "./types";

export const toggleMeterAction =
  (domainCode: string, meterObject: IMeterObject, toggle: boolean) =>
  async (dispatch: ({}) => void) => {
    try {
      const meterObjectToPost: IMeterEventSubscriptionObject[] = [
        {
          PositionID: meterObject.PositionID,
          EventType: meterObject.EventType,
          Enabled: toggle,
        },
      ];
      const resp = await put<any, IMeterEventSubscriptionObject[]>(
        meterEventsSubscriptionsUrl(domainCode),
        meterObjectToPost
      );
      if (!resp) {
        dispatch(actionCreator.fetchingDataError);
        return;
      }
      const meterObjectToDispatch: IMeterObject = {
        ...meterObject,
        Monitoring: toggle,
      };
      dispatch(actionCreator.toggleSingleMeter(meterObjectToDispatch));
    } catch {
      dispatch(actionCreator.fetchingDataError);
    }
  };

export const toggleAllMetersAction =
  (domainCode: string, toggle: boolean) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    dispatch(actionCreator.fetchingMeterData);

    const allMeters = getState().meters.meters;
    try {
      const meterEventSubs = generateMeterItems(allMeters, toggle);

      const resp = await put<any, IMeterEventSubscriptionObject[]>(
        meterEventsSubscriptionsUrl(domainCode),
        meterEventSubs
      );

      if (!resp) {
        dispatch(actionCreator.fetchingDataError);
        return;
      }
      dispatch(actionCreator.toggleMonitoringForAllMeters(toggle));
    } catch {
      dispatch(actionCreator.fetchingDataError);
    }
  };

export const getAllMetersAction =
  (domainCode: string, nodeId: string) => async (dispatch: ({}) => void) => {
    try {
      dispatch(actionCreator.fetchingMeterData);

      const meterEventsSubscription = await get<
        IMeterEventSubscriptionObject[]
      >(meterEventsSubscriptionsUrl(domainCode));
      if (meterEventsSubscription?.length === 0) {
        dispatch(actionCreator.fetchingDataError);
        return;
      }
      const meterEvents = await get<IMeterEventsObject[]>(
        meterEventsUrl(domainCode, nodeId)
      );
      if (meterEvents?.length === 0) {
        dispatch(actionCreator.fetchingDataError);
        return;
      }
      const installations = await get<INodeSensor[]>(
        installationsOnNodeUrl(domainCode, +nodeId, [
          DeviceType.ShellProtection,
        ])
      );

      const measuringDeviceIDs = installations
        .filter((i) => i.MeasuringDeviceID)
        .map((i) => i.MeasuringDeviceID);
      const alarms = await get<IMeasuringDeviceAlert[]>(
        measuringDeviceAlertUrl(domainCode, measuringDeviceIDs)
      );
      const measuringPointsWithActiveAlarms = alarms.reduce<
        { measuringPoint: number; eventID: number }[]
      >((acc, value) => {
        if (value.AlertID !== EventType.Alarm) {
          const measuringPointID = installations.find(
            (i) => i.MeasuringDeviceID === value.MeasuringDeviceID
          )?.MeasuringPointID;
          const eventID = value.AlertID;
          if (!measuringPointID) return acc;
          return [
            ...acc,
            {
              measuringPoint: measuringPointID,
              eventID,
            },
          ];
        }
        return acc;
      }, []);

      const meterObjects = meterEvents
        ?.filter((me) => me.EventType === "Alarm")
        .map((meter) => {
          const monitoring = meterEventsSubscription.find(
            (mes) =>
              mes.PositionID === meter.PositionID && mes.EventType === "Alarm"
          );
          const activeAlarm = measuringPointsWithActiveAlarms.find(
            (m) => m?.measuringPoint === meter.PositionID
          );

          return {
            Name: meter.PositionName,
            PositionID: meter.PositionID,
            EventType: meter.EventType,
            Status: meter.Value,
            Monitoring: monitoring?.Enabled,
            EventTypeID: activeAlarm ? activeAlarm.eventID : EventType.Alarm,
          };
        });
      dispatch(
        actionCreator.fetchingMeterDataSuccess(meterObjects as IMeterObject[])
      );
    } catch {
      dispatch(actionCreator.fetchingDataError);
    }
  };

export const generateMeterItems = (
  allMeters: IMeterObject[],
  checked: boolean
): IMeterEventSubscriptionObject[] => {
  return allMeters.map((a) => {
    return {
      PositionID: a.PositionID,
      EventType: a.EventType,
      Enabled: checked,
    };
  });
};
